import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Icon, Row, Col } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormInput from 'components-desktop/FormInput';
import FormSelect from 'components-desktop/FormSelect';
import FormSelect2 from 'components-desktop/FormSelect2';
import Panel from 'components-desktop/Panel';
import helpers from 'misc/helpers';
import TableCaregivers from './components/TableCaregivers';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class SearchCaregiversPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      companies: [],
      caregiverNames: [],
      coordinators: [],
      teams: [],

      tableKey: null,
      caregivers: null,
      isSearchButtonEnable: true,
    };
  }

  componentDidMount() {
    this.getSearchCriteria();
  }

  getTitle() {
    return 'Search Caregivers';
  }

  async getSearchCriteria() {
    const promise1 = this.callServiceForObjects('/company/get_id_names');
    const promise2 = this.callServiceForObjects('/caregiver/get_id_names');
    const promise3 = this.callServiceForObjects('/coordinator/get_id_names');
    const promise4 = this.callServiceForObjects('/team/get_id_names');

    const results = await Promise.all([promise1, promise2, promise3, promise4]);

    this.setState({
      companies: results[0],
      caregiverNames: results[1],
      coordinators: results[2],
      teams: results[3],
    });
  }

  async searchCaregivers(values) {
    try {
      this.setState({
        isSearchButtonEnable: false,
      });

      const params = {
        companyId: helpers.extraFormSelectValue(values, 'companyId'),
        caregiverId: helpers.extraFormSelectValue(values, 'caregiverId'),
        coordinatorId: helpers.extraFormSelectValue(values, 'coordinatorId'),
        teamId: helpers.extraFormSelectValue(values, 'teamId'),
        birth: helpers.extraFormDateValue(values, 'birth'),
        gender: helpers.extraFormSelectValue(values, 'gender'),
        race: helpers.extraFormSelectValue(values, 'race'),
        ssn: helpers.extraFormInputValue(values, 'ssn'),
        zipCode: helpers.extraFormInputValue(values, 'zipCode'),
        languages: helpers.extraFormSelectValue(values, 'languages'),
        drive: helpers.extraFormSelectValue(values, 'drive'),
        certification: helpers.extraFormSelectValue(values, 'certification'),
        antiSexualTraining: helpers.extraFormSelectValue(values, 'antiSexualTraining'),
        covidVaccine: helpers.extraFormSelectValue(values, 'covidVaccine'),
        paymentMethod: helpers.extraFormSelectValue(values, 'paymentMethod'),
        pickupTeam: helpers.extraFormInputValue(values, 'pickupTeam'),
        waiting: helpers.extraFormSelectValue(values, 'waiting'),
        i9Status: helpers.extraFormSelectValue(values, 'i9Status'),
        benefitCard: helpers.extraFormSelectValue(values, 'benefitCard'),
        applyStatus: helpers.extraFormSelectValue(values, 'applyStatus'),
        applyDateFrom: helpers.extraFormDateValue(values, 'applyDateFrom'),
        applyDateTo: helpers.extraFormDateValue(values, 'applyDateTo'),
      };

      const caregivers = await this.callServiceForRemoteObjects('/caregiver/get_list', params);

      this.setState({
        tableKey: `${Date.now()}_${Math.random()}`,
        caregivers,
      });
    } finally {
      this.setState({
        isSearchButtonEnable: true,
      });
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.searchCaregivers(values);
      }
    });
  }

  renderContent() {
    const { form } = this.props;
    const {
      companies,
      caregiverNames,
      coordinators,
      teams,
      tableKey,
      caregivers,
      isSearchButtonEnable,
    } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="green_haze"
          title="Search Caregivers by Criteria"
          icon="user-add"
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="companyId"
                    label="Company"
                    placeholder="Select..."
                    options={companies}
                    extra="Worked for this company within the last 30 days"
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect2
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="caregiverId"
                    label="Caregiver Name"
                    placeholder="Select..."
                    options={caregiverNames}
                    showIdAfterName
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="coordinatorId"
                    label="Coordinator"
                    placeholder="Select..."
                    options={coordinators}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="teamId"
                    label="Team"
                    placeholder="Select..."
                    options={teams}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="birth"
                    label="Date of Birth"
                    placeholder="mm/dd/yyyy"
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="gender"
                    label="Gender"
                    placeholder="Select..."
                    options={['Female', 'Male']}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="race"
                    label="Race"
                    placeholder="Select..."
                    options={[
                      'Hispanic or Latino',
                      'White (Not Hispanic or Latino)',
                      'Black or African American',
                      'Native Hawaiian or Pacific Islander',
                      'Asian',
                      'Native American or Alaska Native',
                      'Two or more races',
                      'Indeterminable race',
                    ]}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="ssn"
                    label="Social Security Number (SSN)"
                    placeholder="111-11-1111"
                    inputMask="999-99-9999"
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="zipCode"
                    label="ZIP Code"
                    inputMask="99999"
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="languages"
                    label="Language"
                    placeholder="Select..."
                    options={[
                      'English',
                      'Mandarin',
                      'Cantonese',
                      'Spanish',
                      'Japanese',
                      'Korean',
                      'Russian',
                      'Vietnamese',
                      'Fujianese',
                      'Mingnan',
                      'Shanghainese',
                      'Taisanese',
                    ]}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="drive"
                    label="Drive"
                    placeholder="Select..."
                    options={[{ value: 'true', text: 'Yes' }, { value: 'false', text: 'No' }]}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="certification"
                    label="Has Certification"
                    placeholder="Select..."
                    options={[{ value: 'true', text: 'Yes' }, { value: 'false', text: 'No' }]}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="antiSexualTraining"
                    label="Anti-sexual Harassment Training"
                    placeholder="Select..."
                    options={[{ value: 'true', text: 'Yes' }, { value: 'false', text: 'No' }]}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="covidVaccine"
                    label="Covid-19 Vaccination"
                    placeholder="Select..."
                    allowClear
                    options={[
                      { value: '0', text: '0 Dose' },
                      { value: '1', text: '1 Dose' },
                      { value: '2', text: '2 Doses' },
                      { value: '3', text: '3 Doses or More' },
                    ]}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="paymentMethod"
                    label="Payment Method"
                    placeholder="Select..."
                    options={['Check', 'Direct Deposit']}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="pickupTeam"
                    label="Pickup Team"
                    placeholder=""
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="waiting"
                    label="Is Waiting"
                    placeholder="Select..."
                    options={[{ value: 'true', text: 'Yes' }, { value: 'false', text: 'No' }]}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="i9Status"
                    label="I9 Status"
                    placeholder="Select..."
                    options={['No I9 Expire Date', 'About to Expire', 'Expired']}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="benefitCard"
                    label="Benefit Card"
                    placeholder="Select..."
                    options={[{ value: 'true', text: 'Yes' }, { value: 'false', text: 'No' }]}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="applyStatus"
                    label="Apply Status"
                    placeholder="Select..."
                    initialValue="APPROVED"
                    options={['PROCESSING', 'APPROVED', 'REJECTED', 'CANCELED', 'ON_HOLD']}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="applyDateFrom"
                    label="Apply Date From"
                    placeholder="mm/dd/yyyy"
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="applyDateTo"
                    label="Apply Date To"
                    placeholder="mm/dd/yyyy"
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                backgroundColor="green_haze"
                fontSize="14px"
                type="primary"
                loading={!isSearchButtonEnable}
                htmlType="submit"
              >
                SEARCH
                <Icon type="search" />
              </CustomButton>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>

        {caregivers &&
          <TableCaregivers
            key={tableKey}
            width="match_parent"
            height="wrap_content"
            margin="0 0 24px 0"
            title="Caregivers"
            caregivers={caregivers}
          />
        }
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
SearchCaregiversPage.propTypes = propTypes;

export default Form.create()(SearchCaregiversPage);
